/**
 * /topup — buy top-up credit packs.
 *
 * Verbatim from the monolith's `view === "topup"` block. The hand-drawn radio
 * circles are now real radio inputs behind the same pixels — see
 * components/forms/RadioCard.tsx.
 *
 * Top-up packs now send the buyer to external JVZoo checkout. Credits are added only after
 * backend webhook fulfilment confirms the purchase.
 */
import { BackLink, Btn } from '../components/ui';
import { RadioCard, RadioCardGroup } from '../components/forms';
import { JVZOO_TOPUP_URLS, JVZOO_TOPUP_IMAGES } from '../config';
import { PACKS, FRONTEND_PRODUCT } from '../data/products';
import { openExternalUrl } from '../lib/externalNavigation';
import { useCreditsStore } from '../stores/creditsStore';
import { c } from '../styles/theme';
import { paths } from './paths';

export default function Topup() {
  const selectedPack = useCreditsStore((s) => s.selectedPack);
  const setSelectedPack = useCreditsStore((s) => s.setSelectedPack);

  // Falls back to the first pack rather than indexing blindly: the prototype did
  // `PACKS.find(...).credits` in the button label, which throws if the id ever
  // fails to match.
  const pack = PACKS.find((p) => p.id === selectedPack) ?? PACKS[0];

  function openCheckout(): void {
    openExternalUrl(checkoutUrl);
  }

  const checkoutUrl = JVZOO_TOPUP_URLS[pack.id] ?? JVZOO_TOPUP_URLS.p200;

  return (
    <div>
      <BackLink fallbackTo={paths.settings} fallbackLabel="Settings" />

      <h1
        style={{
          fontFamily: 'Space Grotesk',
          fontWeight: 600,
          fontSize: 16,
          margin: '0 0 4px',
          color: c.text1,
        }}
      >
        Credit top-ups
      </h1>
      <p style={{ fontSize: 12, color: c.text2, margin: '0 0 16px' }}>
        Choose a one-time credit pack for your {FRONTEND_PRODUCT.name} account.
      </p>

      <RadioCardGroup label="Credit pack" marginBottom={18}>
        {PACKS.map((p) => (
          <RadioCard
            key={p.id}
            name="topup-pack"
            value={p.id}
            checked={selectedPack === p.id}
            onSelect={setSelectedPack}
            ariaLabel={`${p.credits} credits for $${p.price}`}
            trailing={
              <span style={{ fontSize: 14, fontWeight: 500, fontFamily: 'IBM Plex Mono' }}>
                ${p.price}
              </span>
            }
          >
            <span style={{ display: 'block' }}>
              <span style={{ fontWeight: 500, fontSize: 13, display: 'block' }}>
                {p.credits} credits
                {p.best && (
                  <span
                    style={{
                      background: c.accentSoft,
                      color: c.accent,
                      fontSize: 10,
                      padding: '2px 6px',
                      borderRadius: 4,
                      marginLeft: 6,
                    }}
                  >
                    Best value
                  </span>
                )}
              </span>
              <span
                style={{
                  fontSize: 11,
                  color: c.text3,
                  fontFamily: 'IBM Plex Mono',
                  display: 'block',
                }}
              >
                ${p.per} / credit
              </span>
            </span>
          </RadioCard>
        ))}
      </RadioCardGroup>

      <Btn
        full
        href={checkoutUrl}
        checkoutImageUrl={JVZOO_TOPUP_IMAGES[pack.id]}
        pendingOnNavigate
        onClick={openCheckout}
      >
        {`Buy ${pack.credits} credits — $${pack.price}`}
      </Btn>
      <p style={{ fontSize: 11, color: c.text3, margin: '8px 0 0', textAlign: 'center' }}>
        One-time purchase through JVZoo. Credits are added after payment confirmation and
        never expire.
      </p>
    </div>
  );
}
